import React, { useState, useEffect } from 'react';
import {
  Search, Plus, Eye, Building, Calendar,
  Percent, ArrowUpRight, ArrowDownRight,
  Target, AlertCircle, BarChart3, RefreshCw, CreditCard, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { customerApi, CustomerInvestment, InvestmentListParams } from '@/api/customers';
import { bankApi, Bank } from '@/api/banks';
import toast from 'react-hot-toast';

const InvestmentsPage = () => {
  const [investments, setInvestments] = useState<CustomerInvestment[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'cancelled' | 'pending' | 'deposited'>('all');
  const [bankFilter, setBankFilter] = useState<number | 'all'>('all');
  const [sortBy, setSortBy] = useState<'start_date' | 'amount' | 'interest_rate' | 'maturity_date'>('start_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvestments, setTotalInvestments] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState<CustomerInvestment | null>(null);

  // New investment form
  const [newInvestment, setNewInvestment] = useState({
    bank_id: '',
    amount: '',
    duration_months: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  // Helpers: end date, remaining days, progress
  const getComputedEndDate = (inv: Pick<CustomerInvestment, 'end_date' | 'maturity_date' | 'start_date' | 'duration_months' | 'status'>): Date | null => {
    // Prefer computing from start_date + duration for active investments
    if ((inv as any).status === 'active' && inv.start_date && inv.duration_months && inv.duration_months > 0) {
      try {
        const start = new Date(inv.start_date);
        const end = new Date(start);
        end.setMonth(end.getMonth() + inv.duration_months);
        return end;
      } catch (_) {
        // Fall through to other strategies
      }
    }

    // Fallback: use maturity_date if provided by backend
    if (inv.maturity_date && typeof inv.maturity_date === 'string' && inv.maturity_date.trim() !== '') {
      try {
        return new Date(inv.maturity_date);
      } catch (_) {}
    }

    // Last resort: end_date legacy field
    if (inv.end_date) {
      try {
        return new Date(inv.end_date);
      } catch (_) {}
    }
    return null;
  };

  const getDaysRemainingFromInv = (inv: CustomerInvestment): number | null => {
    // Only calculate for active investments with maturity_date
    if (inv.status !== 'active' || !inv.maturity_date) return null;

    try {
      const maturityDateStr = inv.maturity_date;
      if (typeof maturityDateStr !== 'string') return null;

      const end = new Date(maturityDateStr);
      const now = new Date();
      const diffDays = Math.ceil((end.getTime() - now.getTime()) / (1000 * 3600 * 24));
      return Math.max(0, diffDays); // Don't show negative days
    } catch (e) {
      return null;
    }
  };

  const getProgressPercent = (inv: CustomerInvestment): number => {
    if (!inv.start_date) return 0;
    const start = new Date(inv.start_date);
    const end = getComputedEndDate(inv);
    if (!end) return 0;
    const total = end.getTime() - start.getTime();
    if (total <= 0) return 100;
    const elapsed = Date.now() - start.getTime();
    const pct = Math.round((elapsed / total) * 100);
    return Math.max(0, Math.min(100, pct));
  };

  const getDurationMonths = (inv: CustomerInvestment): number | null => {
    return typeof inv.duration_months === 'number' && inv.duration_months > 0
      ? inv.duration_months
      : null;
  };

  const computeEndFromStartAndDuration = (startDate: string, months: number): Date => {
    const start = new Date(startDate as string);
    const end = new Date(start.getTime());
    end.setMonth(end.getMonth() + months);
    return end;
  };

  // Expected total at maturity: prefer backend expected_return; otherwise simple annual interest prorated by months
  const getExpectedTotal = (inv: CustomerInvestment): number => {
    if (typeof (inv as any).expected_return === 'number') return (inv as any).expected_return as number;
    const months = getDurationMonths(inv) ?? 0;
    const rate = inv.interest_rate || 0;
    const principal = inv.amount || 0;
    const expected = principal * (1 + (rate / 100) * (months / 12));
    return Number.isFinite(expected) ? expected : principal;
  };

  // Fetch investments from API
  const fetchInvestments = async () => {
    try {
      setError(null);
      const params: InvestmentListParams = {
        page: currentPage,
        page_size: itemsPerPage,
      };

      if (statusFilter !== 'all') params.status = statusFilter;
      if (bankFilter !== 'all') params.bank_id = bankFilter;

      const response = await customerApi.investments.list(params);
      setInvestments(response.investments);
      setTotalInvestments(response.total);
      setTotalPages(Math.ceil(response.total / itemsPerPage));
    } catch (err: any) {
      const errorMessage = err.message || 'Fehler beim Laden der Anlagen';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching investments:', err);
    }
  };

  // Fetch banks from API
  const fetchBanks = async () => {
    try {
      const response = await bankApi.list({ is_active: true });
      setBanks(response.banks);
    } catch (err: any) {
      console.error('Error fetching banks:', err);
      toast.error('Fehler beim Laden der Bankenliste');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchInvestments(), fetchBanks()]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentPage, statusFilter, bankFilter]);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchInvestments(), fetchBanks()]);
      toast.success('Daten wurden aktualisiert');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Create investment
  const createInvestment = async () => {
    if (!newInvestment.bank_id || !newInvestment.amount || !newInvestment.duration_months) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    setIsCreating(true);
    try {
      const investmentData = {
        bank_id: parseInt(newInvestment.bank_id),
        amount: parseFloat(newInvestment.amount),
        duration_months: parseInt(newInvestment.duration_months)
      };

      await customerApi.investments.create(investmentData);
      toast.success('Anlage wurde erfolgreich erstellt');

      // Reset form and close modal
      setNewInvestment({ bank_id: '', amount: '', duration_months: '' });
      setShowCreateModal(false);

      // Refresh investments list
      await fetchInvestments();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Erstellen der Anlage');
    } finally {
      setIsCreating(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Backward-compatible calculator (kept for any legacy use)
  const calculateDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 3600 * 24));
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-success-50 text-success-700 border border-success-200';
      case 'deposited': return 'bg-primary-50 text-primary-700 border border-primary-200';
      case 'pending': return 'bg-gold-50 text-gold-700 border border-gold-200';
      case 'inactive': return 'bg-warning-50 text-warning-700 border border-warning-200';
      case 'cancelled': return 'bg-danger-50 text-danger-700 border border-danger-200';
      default: return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'deposited': return 'Eingezahlt';
      case 'pending': return 'Kapitaleingang ausstehend';
      case 'inactive': return 'Inaktiv';
      case 'cancelled': return 'Storniert';
      default: return status;
    }
  };

  // Filter and sort investments
  const filteredInvestments = investments.filter(investment => {
    const matchesSearch = investment.bank_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ? true : investment.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'amount':
        aValue = a.amount;
        bValue = b.amount;
        break;
      case 'interest_rate':
        aValue = a.interest_rate;
        bValue = b.interest_rate;
        break;
      case 'maturity_date':
        aValue = a.maturity_date ? new Date(a.maturity_date as string).getTime() : 0;
        bValue = b.maturity_date ? new Date(b.maturity_date as string).getTime() : 0;
        break;
      default:
        aValue = a.start_date ? new Date(a.start_date).getTime() : 0;
        bValue = b.start_date ? new Date(b.start_date).getTime() : 0;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Anlagen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 border-l-4 border-l-danger-600 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-danger-600 mr-3 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-danger-800">Fehler</h3>
              <p className="text-sm text-danger-700">{error}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            className="text-danger-600 hover:text-danger-800"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-1.5 block">Portfolio</span>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Meine Anlagen</h1>
          <p className="text-slate-500 text-sm mt-1">Verwalten Sie Ihre aktiven Anlagen</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-slate-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Suche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nach Bank suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>

          {/* Durum filtresi gizlendi, yalnızca aktifler listelenir */}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Bank</label>
            <select
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">Alle Banken</option>
              {banks.map(bank => (
                <option key={bank.id} value={bank.id}>{bank.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Sortieren</label>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field as any);
                setSortOrder(order as any);
              }}
              className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="start_date-desc">Neu → Alt</option>
              <option value="start_date-asc">Alt → Neu</option>
              <option value="amount-desc">Betrag (Hoch → Niedrig)</option>
              <option value="amount-asc">Betrag (Niedrig → Hoch)</option>
              <option value="interest_rate-desc">Zins (Hoch → Niedrig)</option>
              <option value="interest_rate-asc">Zins (Niedrig → Hoch)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Investments Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {filteredInvestments.length > 0 ? (
          filteredInvestments.map((investment) => (
            <div key={investment.id} className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
              {/* Investment Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-50 border border-primary-100 flex items-center justify-center overflow-hidden shrink-0">
                    {investment.bank_logo_url ? (
                      <img src={investment.bank_logo_url} alt="bank logo" className="w-10 h-10 object-contain" />
                    ) : (
                      <Building className="w-5 h-5 text-primary-700" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-slate-900">{investment.bank_name}</h3>
                    {/* Yatırım ID bilgisi kaldırıldı */}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(investment.status)}`}>
                  {getStatusText(investment.status)}
                </span>
              </div>

              {/* Investment Details */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Anlagebetrag</span>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">{formatCurrency(investment.amount)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Zinssatz</span>
                  <span className="text-sm font-medium text-success-600 tabular-nums">{parseFloat(String(investment.interest_rate)).toFixed(2)}% p.a.</span>
                </div>

                {/* Pending: show capital input outstanding */}
                {investment.status === 'pending' && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Kapitaleingang ausstehend</span>
                    <span className="text-sm font-medium text-gold-700 tabular-nums">{formatCurrency(investment.amount - investment.current_value)}</span>
                  </div>
                )}

                {/* Only show these fields for active investments */}
                {investment.status === 'active' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Aktueller Wert</span>
                      <span className="text-sm font-medium text-slate-900 tabular-nums">{formatCurrency(investment.current_value)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Erwarteter Ertrag (Laufzeitende)</span>
                      <span className="text-sm font-medium text-success-600 tabular-nums">{formatCurrency(getExpectedTotal(investment))}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Beginn</span>
                      <span className="text-xs text-slate-500">{investment.start_date ? formatDate(investment.start_date) : '-'}</span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Laufzeit</span>
                  <span className="text-xs text-slate-500">{(() => { const m = getDurationMonths(investment); return m === null ? '-' : `${m} Monate`; })()}</span>
                </div>

                {investment.status === 'active' && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Verbleibende Zeit</span>
                    <span className="text-xs text-gold-700">
                      {(() => {
                        const days = getDaysRemainingFromInv(investment);
                        return days === null ? '-' : `${Math.max(0, days)} Tage`;
                      })()}
                    </span>
                  </div>
                )}
              </div>

              {/* Investment Actions */}
              <div className="mt-6 flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={()=> setDetailsModal(investment)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Details
                </Button>

                {/* Rapor butonu kaldırıldı */}
              </div>

              {/* Progress Bar for Active Investments */}
              {investment.status === 'active' && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Fortschritt</span>
                    <span className="tabular-nums">{getProgressPercent(investment)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 h-1.5">
                    <div
                      className="bg-primary-600 h-1.5 transition-all duration-300"
                      style={{ width: `${getProgressPercent(investment)}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-slate-400" />
              </div>
              <h3 className="text-lg font-serif font-medium text-slate-900 mb-2">Noch keine Anlage</h3>
              <p className="text-slate-500 mb-6">
                Tätigen Sie Ihre erste Anlage, um zu starten.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 px-6 py-4">
          <div className="text-sm text-slate-500">
            Anzeige {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalInvestments)} von {totalInvestments} Anlagen
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Zurück
            </Button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Weiter
            </Button>
          </div>
        </div>
      )}

      {/* Create Investment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-serif font-medium text-slate-900">Neue Anlage</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewInvestment({ bank_id: '', amount: '', duration_months: '' });
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Investment Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Bank</label>
                  <select
                    value={newInvestment.bank_id}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, bank_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    <option value="">Bank auswählen</option>
                    {banks.map(bank => (
                      <option key={bank.id} value={bank.id}>{bank.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Anlagebetrag</label>
                  <input
                    type="number"
                    value={newInvestment.amount}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="€0"
                    className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Laufzeit (Monate)</label>
                  <select
                    value={newInvestment.duration_months}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, duration_months: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    <option value="">Laufzeit wählen</option>
                    <option value="3">3 Monate</option>
                    <option value="6">6 Monate</option>
                    <option value="12">12 Monate</option>
                    <option value="24">24 Monate</option>
                    <option value="36">36 Monate</option>
                  </select>
                </div>
              </div>

              {/* Create Button */}
              {newInvestment.bank_id && newInvestment.amount && newInvestment.duration_months && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={createInvestment}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-white/40 border-t-white mr-2"></div>
                      Wird erstellt...
                    </div>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Anlage bestätigen
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsModal && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 w-full max-w-lg">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-serif font-medium text-slate-900">Anlagedetails</h3>
              <Button variant="ghost" size="sm" onClick={()=> setDetailsModal(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Bank</span><span className="font-medium text-slate-900">{detailsModal.bank_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Betrag</span><span className="font-medium text-slate-900 tabular-nums">{formatCurrency(detailsModal.amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Zinssatz</span><span className="font-medium text-slate-900 tabular-nums">{parseFloat(String(detailsModal.interest_rate)).toFixed(2)}% p.a.</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Beginn</span><span className="text-slate-700">{detailsModal.start_date ? formatDate(detailsModal.start_date) : '-'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Laufzeitende</span><span className="text-slate-700">{(detailsModal as any).maturity_date ? formatDate((detailsModal as any).maturity_date as string) : (detailsModal.end_date ? formatDate(detailsModal.end_date) : '-')}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={`px-2 py-0.5 text-xs ${getStatusColor(detailsModal.status)}`}>{getStatusText(detailsModal.status)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentsPage;
